import {
  initializePaddle,
  CheckoutEventNames,
  type CheckoutOpenOptions,
  type Paddle,
} from "@paddle/paddle-js";

/// Price ids are per-environment, so they are config, not catalog data.
const PRICE_IDS: Record<string, { month?: string; year?: string }> = {
  pro: {
    month: import.meta.env.VITE_APP_PADDLE_PRICE_PRO_MONTH,
    year: import.meta.env.VITE_APP_PADDLE_PRICE_PRO_YEAR,
  },
};

export type BillingInterval = "month" | "year";

const CLIENT_TOKEN = import.meta.env.VITE_APP_PADDLE_CLIENT_TOKEN;
const ENVIRONMENT = import.meta.env.VITE_APP_PADDLE_ENVIRONMENT;

/// Unset ⇒ nothing is offered for sale.
export function isCheckoutConfigured(): boolean {
  return Boolean(CLIENT_TOKEN);
}

/// Paddle's dunning emails link to `<site>?_ptxn=txn_…` (the default payment
/// link). Read at boot and opened explicitly: the router redirects a signed-in
/// visitor off `/` and drops the query before Paddle.js's async init reads it,
/// and stripping it first keeps Paddle.js from opening the same form twice.
export function openPaymentLinkIfPresent(): void {
  if (!isCheckoutConfigured()) return;
  try {
    const url = new URL(window.location.href);
    const transactionId = url.searchParams.get("_ptxn");
    if (!transactionId?.startsWith("txn_")) return;
    url.searchParams.delete("_ptxn");
    window.history.replaceState(window.history.state, "", url.toString());
    void paddleClient()
      .then((client) => client?.Checkout.open({ transactionId }))
      .catch(() => undefined);
  } catch {
    // An unparseable URL or a missing Paddle config opens nothing; the app
    // itself is unaffected.
  }
}

export function priceIdFor(
  planCode: string,
  interval: BillingInterval,
): string | undefined {
  return PRICE_IDS[planCode]?.[interval];
}

let paddle: Promise<Paddle | undefined> | null = null;

/// The class Paddle renders its iframe into (`frameTarget` takes a class).
export const CHECKOUT_FRAME_TARGET = "paddle-checkout-frame";

/// Paddle.js takes ONE callback at initialization for every checkout, so
/// per-open handlers are parked here.
let handlers: { onLoaded?: () => void; onCompleted?: () => void } = {};

/// Throws on a missing environment rather than default it: a token alone cannot
/// say which Paddle account it bills.
function paddleClient(): Promise<Paddle | undefined> {
  if (!CLIENT_TOKEN) {
    throw new Error("VITE_APP_PADDLE_CLIENT_TOKEN is unset");
  }
  if (ENVIRONMENT !== "sandbox" && ENVIRONMENT !== "production") {
    throw new Error(
      `VITE_APP_PADDLE_ENVIRONMENT must be "sandbox" or "production", got ${
        ENVIRONMENT ?? "nothing"
      }`,
    );
  }
  paddle ??= initializePaddle({
    token: CLIENT_TOKEN,
    environment: ENVIRONMENT,
    eventCallback: (event) => {
      if (event.name === CheckoutEventNames.CHECKOUT_LOADED) {
        handlers.onLoaded?.();
      }
      if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
        const { onCompleted } = handlers;
        handlers = {};
        onCompleted?.();
      }
    },
  });
  return paddle;
}

export interface CheckoutRequest {
  priceId: string;
  /// Seats bought, not the org's member count.
  quantity: number;
  /// Prefilled and pinned: the buyer cannot move the checkout to another address.
  email?: string;
  /// Fires in-page on `checkout.completed`; nothing navigates.
  onCompleted: () => void;
  onLoaded?: () => void;
  /// Rides the transaction for attribution: the server spends `checkout_token`
  /// and treats `org_id` as a hint that must agree; `org_kind`/`org_name` are
  /// for recovery after a wipe.
  customData: {
    org_id: string;
    plan_code: string;
    checkout_token: string;
    org_kind: "personal" | "company";
    org_name: string;
  };
}

/// The theme store stamps the resolved theme on `<html>`.
function appTheme(): "dark" | "light" {
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

/// Paddle's floor is 312px wide; it drives the height itself after this.
const INLINE_FRAME: NonNullable<CheckoutOpenOptions["settings"]> = {
  displayMode: "inline",
  variant: "one-page",
  frameTarget: CHECKOUT_FRAME_TARGET,
  frameInitialHeight: 450,
  frameStyle:
    "width: 100%; min-width: 312px; background-color: transparent; border: none;",
};

/// No `successUrl`: `checkout.completed` confirms in place.
export async function openCheckout({
  priceId,
  quantity,
  email,
  onCompleted,
  onLoaded,
  customData,
}: CheckoutRequest): Promise<void> {
  const client = await paddleClient();
  if (!client) throw new Error("Paddle.js failed to initialize");
  handlers = { onCompleted, onLoaded };
  const customer = email ? { email } : undefined;
  const options: CheckoutOpenOptions = {
    items: [{ priceId, quantity }],
    customData,
    settings: {
      ...INLINE_FRAME,
      theme: appTheme(),
      allowLogout: customer === undefined,
    },
  };
  if (customer) {
    options.customer = customer;
  }
  client.Checkout.open(options);
}

export async function openCardForm({
  transactionId,
  onCompleted,
  onLoaded,
}: {
  transactionId: string;
  onCompleted: () => void;
  onLoaded?: () => void;
}): Promise<void> {
  const client = await paddleClient();
  if (!client) throw new Error("Paddle.js failed to initialize");
  handlers = { onCompleted, onLoaded };
  client.Checkout.open({
    transactionId,
    settings: { ...INLINE_FRAME, theme: appTheme() },
  });
}

/// Otherwise the iframe outlives its element and the next open finds Paddle
/// still holding one.
export async function closeCheckout(): Promise<void> {
  handlers = {};
  const client = await paddleClient();
  // Closing an already-closed checkout throws; every exit path calls this.
  try {
    client?.Checkout.close();
  } catch {
    /* empty */
  }
}

/// Paddle's localized totals keyed by price id (geolocated, no country passed).
/// Display verbatim so the figure matches what checkout charges.
export async function previewPrices(
  priceIds: string[],
  quantity: number,
): Promise<Record<string, string>> {
  const client = await paddleClient();
  if (!client) throw new Error("Paddle.js failed to initialize");
  const preview = await client.PricePreview({
    items: priceIds.map((priceId) => ({ priceId, quantity })),
  });
  return Object.fromEntries(
    preview.data.details.lineItems.map((line) => [
      line.price.id,
      line.formattedTotals.total,
    ]),
  );
}
